import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { FilterField } from '@ukam/core/filters/models/filter-field';

interface DecimalFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

// DecimalFilter.cs only supports range/comparison operators — no Eq/Ne/In/Nin.
const DECIMAL_FILTER_OPERATORS: DecimalFilterOperatorOption[] = [
  { value: 'btw', label: 'Between' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal to' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal to' },
];

@Component({
  selector: 'app-decimal-filter',
  templateUrl: './decimal-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppDecimalFilterComponent), multi: true },
  ],
})
export class AppDecimalFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = DECIMAL_FILTER_OPERATORS;

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value: number | null = null;
  start: number | null = null;
  end: number | null = null;

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(800)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isRange(): boolean {
    return this.operator === 'btw';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    this.operator = op;
    this.value = null;
    this.start = null;
    this.end = null;
    this.emit({ operator: op, value: undefined, start: undefined, end: undefined });
  }

  onValueChange(value: number | null): void {
    this.value = value;
    this.emit({ value: value == null ? undefined : `${value}` });
  }

  onStartChange(value: number | null): void {
    this.start = value;
    this.emit({ start: value == null ? undefined : `${value}` });
  }

  onEndChange(value: number | null): void {
    this.end = value;
    this.emit({ end: value == null ? undefined : `${value}` });
  }

  clear(): void {
    this.value = null;
    this.start = null;
    this.end = null;
    this.emit({ value: undefined, start: undefined, end: undefined });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.value = this.toNumber(this.field?.value);
    this.start = this.toNumber(this.field?.start);
    this.end = this.toNumber(this.field?.end);
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private toNumber(raw: string | undefined | null): number | null {
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
}
